import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createNotificationMethod,
  deleteNotificationMethod,
  getNotificationMethodById,
  getNotificationMethods,
  updateNotificationMethod,
} from './notification-settings.api';
import type {
  CreateNotificationMethodFormValues,
  NotificationMethodsFilters,
  UpdateNotificationMethodFormValues,
} from './notification-settings.types';

export const useNotificationMethodsQuery = (filters?: NotificationMethodsFilters) =>
  useQuery({
    queryKey: ['notification-methods', filters],
    queryFn: () => getNotificationMethods(filters),
  });

export const useNotificationMethodQuery = (id: string) =>
  useQuery({
    queryKey: ['notification-methods', id],
    queryFn: () => getNotificationMethodById(id),
    enabled: !!id,
  });

export const useCreateNotificationMethodMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateNotificationMethodFormValues) => createNotificationMethod(values),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['notification-methods'] });
    },
  });
};

export const useUpdateNotificationMethodMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateNotificationMethodFormValues) => updateNotificationMethod(values),
    onSuccess(_, { id }) {
      queryClient.invalidateQueries({ queryKey: ['notification-methods'] });
      queryClient.invalidateQueries({ queryKey: ['notification-methods', id] });
    },
  });
};

export const useDeleteNotificationMethodMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteNotificationMethod(id),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['notification-methods'] });
    },
  });
};
